import type { Accommodation } from '../types';

/**
 * Build a Booking.com search URL for an accommodation.
 *
 * Why not link to each property's own website?
 *
 *   Stay22's Link Monetization Automation (LMA) script can only rewrite
 *   outbound links that point at one of its supported booking partners
 *   — Booking.com, Hotels.com, Expedia, Agoda, Airbnb, Vrbo, etc. It has
 *   no way to monetize a click through to, say, `hotelvt.com`, because
 *   the hotel's own domain is not part of any affiliate network we're a
 *   member of. Users who go straight to the hotel's site complete the
 *   reservation there and the trail goes cold — we earn $0.
 *
 *   Linking to a Booking.com search keyed on the property's name + city
 *   puts the click inside a Stay22-supported domain, so LMA transparently
 *   rewrites it into an affiliate-tagged URL on click. The user still
 *   ends up at a usable booking flow even if we haven't activated LMA
 *   yet; commission starts flowing the moment the `<script>` in
 *   `index.html` is enabled.
 *
 *   If a specific stay isn't on Booking.com the search gracefully shows
 *   nearby alternatives, which is arguably better for the user than
 *   nothing. We never link straight to the property's own homepage.
 */
export function bookingSearchUrl(stay: Accommodation): string {
  const query = [stay.name, stay.city, stay.state].filter(Boolean).join(', ');
  const params = new URLSearchParams({ ss: query });
  // Booking.com's public search entrypoint. Undocumented but stable.
  return `https://www.booking.com/searchresults.html?${params.toString()}`;
}
